import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';

export default class ErrorMessagesService extends Service {
  @tracked activeErrors = new Map();

  /**
   * Error message templates organized by context and error type
   */
  messageTemplates = {
    // Network/connection errors
    network: {
      offline: {
        title: 'No Internet Connection',
        message: 'Please check your internet connection and try again.',
        action: 'Check Connection',
        severity: 'error',
      },
      timeout: {
        title: 'Request Timed Out',
        message: 'The server is taking too long to respond. Please try again.',
        action: 'Retry',
        severity: 'warning',
      },
      serverUnavailable: {
        title: 'Service Unavailable',
        message:
          "Our servers are currently experiencing issues. We're working to fix this.",
        action: 'Try Again Later',
        severity: 'error',
      },
    },

    // HTTP status errors
    http: {
      400: {
        title: 'Invalid Request',
        message:
          'There was a problem with your request. Please check your input and try again.',
        action: 'Review & Retry',
        severity: 'warning',
      },
      401: {
        title: 'Authentication Required',
        message: 'Please log in to access this content.',
        action: 'Sign In',
        severity: 'info',
      },
      403: {
        title: 'Access Denied',
        message: "You don't have permission to access this content.",
        action: 'Contact Support',
        severity: 'warning',
      },
      404: {
        title: 'Content Not Found',
        message:
          "The content you're looking for doesn't exist or may have been moved.",
        action: 'Go Home',
        severity: 'info',
      },
      429: {
        title: 'Too Many Requests',
        message:
          "You're making requests too quickly. Please wait a moment and try again.",
        action: 'Wait & Retry',
        severity: 'warning',
      },
      500: {
        title: 'Server Error',
        message: 'Something went wrong on our end. Our team has been notified.',
        action: 'Try Again',
        severity: 'error',
      },
      502: {
        title: 'Service Temporarily Unavailable',
        message:
          'Our service is temporarily down for maintenance. Please try again shortly.',
        action: 'Try Later',
        severity: 'error',
      },
      503: {
        title: 'Service Unavailable',
        message:
          'Our service is currently unavailable. Please try again in a few minutes.',
        action: 'Try Later',
        severity: 'error',
      },
    },

    // Context-specific errors
    content: {
      loadFailed: {
        title: 'Failed to Load Content',
        message:
          "We couldn't load the requested content. This might be a temporary issue.",
        action: 'Retry',
        severity: 'warning',
      },
      searchFailed: {
        title: 'Search Unavailable',
        message: 'Search is temporarily unavailable. Please try again later.',
        action: 'Try Again',
        severity: 'warning',
      },
      dataCorrupted: {
        title: 'Data Error',
        message: 'The content appears to be corrupted or incomplete.',
        action: 'Refresh Page',
        severity: 'error',
      },
    },

    // User action errors
    user: {
      formInvalid: {
        title: 'Form Validation Error',
        message: 'Please check the highlighted fields and correct any errors.',
        action: 'Review Form',
        severity: 'warning',
      },
      sessionExpired: {
        title: 'Session Expired',
        message: 'Your session has expired. Please sign in again to continue.',
        action: 'Sign In',
        severity: 'info',
      },
      uploadFailed: {
        title: 'Upload Failed',
        message:
          "We couldn't upload your file. Please check the file size and format.",
        action: 'Try Again',
        severity: 'error',
      },
    },

    // GraphQL specific errors
    graphql: {
      queryFailed: {
        title: 'Data Load Error',
        message:
          'We encountered an issue loading the data. Please try refreshing the page.',
        action: 'Refresh',
        severity: 'warning',
      },
      mutationFailed: {
        title: 'Save Failed',
        message: "We couldn't save your changes. Please try again.",
        action: 'Retry',
        severity: 'error',
      },
      validationError: {
        title: 'Validation Error',
        message: 'Please check your input and correct any validation errors.',
        action: 'Review Input',
        severity: 'warning',
      },
    },
  };

  /**
   * Gets an appropriate error message for the given error
   * @param {Error} error - The error object
   * @param {string} context - The context where the error occurred
   * @param {Object} options - Additional options
   * @returns {Object} Formatted error message
   */
  getErrorMessage(error, context = 'general', options = {}) {
    let messageTemplate = this.findMessageTemplate(error, context);

    // Apply customizations
    if (options.customMessage) {
      messageTemplate = { ...messageTemplate, message: options.customMessage };
    }

    if (options.customTitle) {
      messageTemplate = { ...messageTemplate, title: options.customTitle };
    }

    if (options.customAction) {
      messageTemplate = { ...messageTemplate, action: options.customAction };
    }

    // Add context-specific information
    return {
      ...messageTemplate,
      error,
      context,
      timestamp: new Date().toISOString(),
      id: this.generateErrorId(),
      technical: this.extractTechnicalDetails(error),
      suggestions: this.getSuggestions(error, context),
    };
  }

  /**
   * Finds the most appropriate message template for an error
   * @param {Error} error - The error object
   * @param {string} context - The error context
   * @returns {Object} Message template
   */
  findMessageTemplate(error, context) {
    // Check for HTTP status codes
    if (error.status) {
      const httpTemplate = this.messageTemplates.http[error.status];
      if (httpTemplate) return httpTemplate;
    }

    // Check for network errors
    if (this.isNetworkError(error)) {
      if (this.isOffline()) {
        return this.messageTemplates.network.offline;
      }
      if (this.isTimeoutError(error)) {
        return this.messageTemplates.network.timeout;
      }
      return this.messageTemplates.network.serverUnavailable;
    }

    // Check for GraphQL errors
    if (error.graphQLErrors || error.networkError) {
      return this.messageTemplates.graphql.queryFailed;
    }

    // Check context-specific templates
    const contextTemplate = this.messageTemplates[context];
    if (contextTemplate) {
      // Try to find a specific template based on error characteristics
      if (error.name === 'ValidationError' && contextTemplate.validationError) {
        return contextTemplate.validationError;
      }
      if (contextTemplate.loadFailed) {
        return contextTemplate.loadFailed;
      }
    }

    // Default fallback
    return {
      title: 'Something Went Wrong',
      message:
        'An unexpected error occurred. Please try again or contact support if the problem persists.',
      action: 'Try Again',
      severity: 'error',
    };
  }

  /**
   * Registers an active error for tracking
   * @param {string} errorId - Unique error identifier
   * @param {Object} errorMessage - Error message object
   */
  registerError(errorId, errorMessage) {
    this.activeErrors.set(errorId, {
      ...errorMessage,
      registeredAt: new Date(),
    });
  }

  /**
   * Dismisses an active error
   * @param {string} errorId - Error identifier to dismiss
   */
  dismissError(errorId) {
    this.activeErrors.delete(errorId);
  }

  /**
   * Gets all active errors
   * @returns {Array} Array of active errors
   */
  getActiveErrors() {
    return Array.from(this.activeErrors.values());
  }

  /**
   * Clears all active errors
   */
  clearAllErrors() {
    this.activeErrors.clear();
  }

  /**
   * Gets contextual suggestions for resolving an error
   * @param {Error} error - The error object
   * @param {string} context - Error context
   * @returns {Array} Array of suggestions
   */
  getSuggestions(error, context) {
    const suggestions = [];

    if (this.isNetworkError(error)) {
      suggestions.push('Check your internet connection');
      suggestions.push('Try refreshing the page');
      if (this.isOffline()) {
        suggestions.push('Connect to the internet and try again');
      }
    }

    if (error.status === 429) {
      suggestions.push('Wait a moment before trying again');
      suggestions.push('Consider reducing the frequency of your requests');
    }

    if (error.status >= 500) {
      suggestions.push('Try again in a few minutes');
      suggestions.push('Contact support if the issue persists');
    }

    if (context === 'search') {
      suggestions.push('Try different search terms');
      suggestions.push('Check your spelling');
    }

    return suggestions;
  }

  /**
   * Extracts technical details from an error for debugging
   * @param {Error} error - The error object
   * @returns {Object} Technical details
   */
  extractTechnicalDetails(error) {
    return {
      name: error.name,
      message: error.message,
      status: error.status,
      stack: error.stack,
      timestamp: new Date().toISOString(),
    };
  }

  /**
   * Checks if an error is a network-related error
   * @param {Error} error - The error object
   * @returns {boolean} True if it's a network error
   */
  isNetworkError(error) {
    return (
      error.name === 'NetworkError' ||
      (error.name === 'TypeError' && error.message.includes('fetch')) ||
      error.message?.includes('fetch') ||
      error.message?.includes('network') ||
      !error.status // No status usually means network issue
    );
  }

  /**
   * Checks if an error is a timeout error
   * @param {Error} error - The error object
   * @returns {boolean} True if it's a timeout error
   */
  isTimeoutError(error) {
    return (
      error.name === 'AbortError' ||
      error.message?.includes('timeout') ||
      error.status === 408
    );
  }

  /**
   * Checks if the user is offline
   * @returns {boolean} True if offline
   */
  isOffline() {
    return !navigator.onLine;
  }

  /**
   * Generates a unique error ID
   * @returns {string} Unique identifier
   */
  generateErrorId() {
    return `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}
